// NodeUploadHelpers.js

/**
 * Extracts intrinsic media metadata from an uploaded object path or URL.
 *
 * @param {string} mediaUrl - Object path or URL for the uploaded media.
 * @param {object} item - Node-like object to mutate with metadata fields.
 *
 * @returns {Promise<object>} Resolves the same item with metadata fields applied.
 */
export const enrichItemMetadataFromUrl = async (mediaUrl, item) => {
  const isVideo = item.type === 'VIDEO';

  if (isVideo) {
    return item;
  }

  return new Promise((resolve, reject) => {

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(1, 1);
      canvas.getContext('2d');
      const img = new Image();

      img.crossOrigin = 'anonymous';
      img.src = mediaUrl;

      const timeout = setTimeout(() => {
        reject(new Error('Metadata load timed out after 10 seconds'));
      }, 10000);

      img.onload = () => {
        clearTimeout(timeout);
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;
        item.aspectRatio = img.naturalWidth / img.naturalHeight;
        resolve(item);
      };

      img.onerror = (event) => {
        clearTimeout(timeout);
        reject(new Error(`Failed to load image metadata: ${event.type || 'unknown error'}`));
      };

      return;
    }

    const el = document.createElement(isVideo ? 'video' : 'img');
    el.src = mediaUrl;
    el.crossOrigin = 'anonymous';
    el.style.display = 'none';
    el.preload = 'metadata';
    el.muted = true;

    const container = document.createElement('div');
    container.style.display = 'none';
    container.appendChild(el);
    document.body.appendChild(container);

    const cleanup = () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error(`Metadata load timed out after 10 seconds for ${isVideo ? 'video' : 'image'}`));
    }, 10000);

    const onSuccess = () => {
      clearTimeout(timeout);

      try {
        const width = isVideo ? el.videoWidth : el.naturalWidth;
        const height = isVideo ? el.videoHeight : el.naturalHeight;

        if (width > 0 && height > 0) {
          item.width = width;
          item.height = height;
          item.aspectRatio = width / height;
          cleanup();
          resolve(item);
          return;
        }

        throw new Error('Invalid dimensions received');
      } catch (error) {
        cleanup();
        reject(new Error(`Failed to extract ${isVideo ? 'video' : 'image'} dimensions: ${error.message}`));
      }
    };

    el.onload = onSuccess;
    if (isVideo) {
      el.onloadedmetadata = onSuccess;
    }

    el.onerror = (event) => {
      clearTimeout(timeout);
      cleanup();
      reject(new Error(`Failed to load ${isVideo ? 'video' : 'image'} metadata (${event.type || 'unknown'})`));
    };

    if (isVideo) {
      el.onabort = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error('Video metadata loading aborted'));
      };

      el.onstalled = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error('Video metadata loading stalled'));
      };
    }
  });
};

/**
 * Generates a secure random folder name for storing node media.
 *
 * @returns {string} A 32-character hex string.
 */
export const generateSecureFolder = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};